// Bridges the pure place.ts engine (which knows only Box{id,x,y,w,h}) to the
// app's WidgetInstance + registry (which knows each widget's effective w/h). Kept
// separate so place.ts stays registry-free and trivially testable.
import { getWidgetDef } from './registry';
import { widgetWH, clampToBounds, GRID_COLS } from './resize';
import { derivePositions, type Box } from './place';
import type { WidgetInstance, HomeLayout } from './types';

// Effective footprint for a widget: its stored w/h (or the def's defaultWH when
// unsized), clamped into the def's resize bounds (e.g. KPI never exceeds 2x1).
function footprint(w: WidgetInstance): { w: number; h: number } {
  const def = getWidgetDef(w.type);
  const sized = typeof w.w === 'number' && typeof w.h === 'number';
  const base = sized ? widgetWH(w) : (def?.defaultWH ?? widgetWH(w));
  return clampToBounds(base, def?.bounds);
}

// Resolve one CONTAINER's widgets (array order) into placed Box[] with explicit
// x/y filled in (auto-flow parity for any widget lacking a position).
export function resolveContainerBoxes(widgets: WidgetInstance[]): Box[] {
  const raw: Box[] = widgets.map((w) => {
    const fp = footprint(w);
    return {
      id: w.id,
      x: typeof w.x === 'number' && Number.isFinite(w.x) ? w.x : NaN,
      y: typeof w.y === 'number' && Number.isFinite(w.y) ? w.y : NaN,
      w: fp.w, h: fp.h,
    };
  });
  return derivePositions(raw, GRID_COLS);
}

// Write resolved x/y from boxes back onto the matching widgets; widgets absent
// from `boxes` (other containers) are left untouched. (Resize writes w/h itself
// in HomePage, so this only carries positions.)
export function applyBoxes(widgets: WidgetInstance[], boxes: Box[]): WidgetInstance[] {
  const m = new Map(boxes.map((b) => [b.id, b]));
  return widgets.map((w) => {
    const b = m.get(w.id);
    return b ? { ...w, x: b.x, y: b.y } : w;
  });
}

// Idempotently fill x/y for every widget so a saved layout is fully explicit
// (called before persist; harmless if already positioned).
export function ensurePositions(layout: HomeLayout): HomeLayout {
  return { ...layout, widgets: applyBoxes(layout.widgets, resolveContainerBoxes(layout.widgets)) };
}
